import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import API from "../../../utils/API";
import MesTable from "../../../components/Table/MesTable";
import tableCheckbox from "../../../components/Inputs/tableCheckbox";
import constants from "../../../utils/constants";
import useInfiniteScroll from "../../../components/common/useInfiniteScroll";
import NotificationModal from "../../../components/MessageManager/NotificationModal";
import deAssign from "../../../assets/icons/message-manager/de-assign.svg";
import assign from "../../../assets/icons/message-manager/assign.svg";
import add from "../../../assets/icons/message-manager/add.svg";
import totalGroups from "../../../assets/icons/message-manager/totalGroups.svg";
import GroupTree from "./GroupTree";
import AddNewGroup from "./AddNewGroup";
import AssignUsersToGroupModal from "./AssignUsersToGroupModal";
import ViewReceiverUsersModal from "./ViewReceiverUsersModal";

function GroupManagement(props) {
  const navigate = useNavigate();
  const { user } = props;
  const permissionIds = user.permissions.map((permission) => permission.id);

  if (!permissionIds.includes(constants.permissionIds.msgManagerEdit)) {
    navigate("/forbidden");
  }

  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState('');
  const [sortByAsc, setSortByAsc] = useState();
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [receiversData, setReceiversData] = useState([]);
  const [checkedKey, setCheckedKey] = useState([]);
  const [selectedKey, setSelectedKey] = useState("-1");
  const [groupsTreeData, setGroupsTreeData] = useState([]);
  const [users, setUsers] = useState([]);
  const tableRef = useRef();
  const tableWrapperRef = useRef();
  const modalRef = useRef(null);
  const resetAssignUsersToGroupModalRef = useRef(null);
  const [notificationProps, setNotificationProps] = useState({
    message: "ASSIGN_RECEIVERS_TIPS",
    description: "ASSIGN_RECEIVERS_INFO",
    primaryButtonText: "CONTINUE",
    className: "green",
  });
  const [selectedReceiver, setSelectedReceiver] = useState(null);
  const [selectedReceiverName, setSelectedReceiverName] = useState(null);
  const resetViewReceiverUsersModalRef = useRef(null);

  // Load users for the assign users to group modal
  const loadUsers = async () => {
    try {
      const response = await API.getMessengerUsers();
      setUsers(response.data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadTable = async (map, resetSelection = true) => {
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }
    try {
      const res = await API.getMessengerReceivers(selectedKey);
      console.log(res.data);
      res.data.forEach((receivers) => {
      // Calculate status based on sbmm_last_seen and in-process status
      const lastSeen = new Date(receivers.sbmmLastSeen || 0);
      const currentTime = new Date();
      const fiveMinutesAgo = new Date(currentTime - 5 * 60 * 1000); // 5 minutes ago

      let status = "offline";
      if (receivers.inProcess) {
        status = "in_process";
      } else if (lastSeen > fiveMinutesAgo) {
        status = "online";
      }

      map.data[receivers.mac] = {
        mac: receivers.mac,
        name: receivers.name,
        sitesName: receivers.sites.name,
        groupsName: receivers.groups.map((group) => group.name).join(","),
        status: status,
        checked: false,
      };
      });
      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
      setReceiversData(res.data);
      setDataCount(map.ids.length);
      setSelectedIds(map.selectedIds);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);
    } catch (err) {
      setError(err.message);
      setDataLoaded(true);
    }
  };

  const closeModal = (modalId) => {
    const modal = document.getElementById(modalId);
    if (modal) {
      const closeButton = modal.querySelector(
        '.btn-close, [data-bs-dismiss="modal"]'
      );
      if (closeButton) {
        closeButton.click();
      }
    }
    fetchGroupsTreeData();
  };

  const dataMap = useMemo(() => {
    const map = {
      data: {},
      ids: [],
      selectedIds: {},
      filteredIds: [],
      sortedIds: [],
    };
    loadTable(map);
    return map;
  }, []);

  const parseGroupsData = (groupsData) => {
    return groupsData.map((node) => ({
      title: node.name,
      key: node.id,
    }));
  };

  const fetchGroupsTreeData = async () => {
    const groups = await API.getGroups();
    const parsedData = parseGroupsData(groups.data);
    console.log('groups.data', groups.data)
    setGroupsTreeData(parsedData);
  };

  useEffect(() => {
    modalRef.current = new window.bootstrap.Modal(
      document.getElementById("notificationModal")
    );
    fetchGroupsTreeData();
  }, []);

  // Effect for selectedKey changes
  useEffect(() => {
    console.log("selectedKey changed", selectedKey);
    loadTable(dataMap);
  }, [selectedKey]);
  
  // Effect for interval refresh (runs once when component mounts)
  useEffect(() => {
    console.log("Setting up interval refresh");
    
    const intervalId = setInterval(() => {
      console.log("Refreshing receiver status data on interval");
      loadTable(dataMap, false); // Don't reset selection on refresh
    }, 30000); // 30 seconds
    
    return () => {
      console.log("Clearing interval refresh");
      clearInterval(intervalId);
    };
  }, []);

  const handleDeleteGroup = async (key) => {
    setNotificationProps({
      icon: totalGroups,
      message: "DELETE_GROUP_TIPS",
      description: "DELETE_GROUP_INFO",
      showSecondaryButton: true,
      primaryButtonText: "CONTINUE",
      className: "red",
      onSubmit: async () => {
        try {
          await API.deleteGroup(key);
          const updatedGroups = groupsTreeData.filter((group) => group.key !== key);
          setGroupsTreeData(updatedGroups);
          setSelectedKey("-1");
          modalRef.current.hide();
          setSuccess(<FormattedMessage id="DELETE_GROUP_SUCCESS" />);
        } catch (error) {
          console.error("Error deleting group:", error);
          setError(<FormattedMessage id="DELETE_GROUP_FAILED" values={{ errorMsg: <b>{error.message}</b> }} />);
        }
      },
    });
    modalRef.current.show();
  };

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
    rowHeight: 48,
  });

  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== "") {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, receiversData]);

  const assignGroupProps = {
    icon: assign,
    message: "ASSIGN_RECEIVERS_TIPS",
    description: "ASSIGN_RECEIVERS_INFO",
    showSecondaryButton: true,
    primaryButtonText: "CONTINUE",
    className: "green",
    onSubmit: () =>
      handleGroupAssignment(
        API.bindGroupsReceivers,
        "ASSIGN_RECEIVERS_SUCCESS",
        "ASSIGN_RECEIVERS_FAILED"
      ),
  };

  const deAssignGroupProps = {
    icon: deAssign,
    message: "DE_ASSIGN_RECEIVERS_TIPS",
    description: "DE_ASSIGN_RECEIVERS_INFO",
    showSecondaryButton: true,
    primaryButtonText: "CONTINUE",
    className: "red",
    onSubmit: () =>
      handleGroupAssignment(
        API.unbindGroupsReceivers,
        "DE_ASSIGN_RECEIVERS_SUCCESS",
        "DE_ASSIGN_RECEIVERS_FAILED"
      ),
  };

  const handleGroupAssignment = async (
    apiMethod,
    successMessageId,
    errorMessageId
  ) => {
    try {
      await apiMethod(checkedKey, Object.keys(selectedIds));
      setSuccess(<FormattedMessage id={successMessageId} />);
    } catch (err) {
      setError(
        <FormattedMessage
          id={errorMessageId}
          values={{ errorMsg: <b>{err.message}</b> }}
        />
      );
    }
    loadTable(dataMap);
    modalRef.current.hide();
  };

  const setResetAssignUsersToGroupModalRef = (resetFunc) => {
    resetAssignUsersToGroupModalRef.current = resetFunc;
  };

  // Add a direct handler for opening the assign users modal
  const handleAssignUsersToGroupClick = () => {
    // Open the assign users to group modal directly
    const modal = new window.bootstrap.Modal(
      document.getElementById("assignUsersToGroupModal")
    );
    modal.show();
  };

  // Add this function to handle view users click
  const handleViewUsersClick = (receiver) => {
    console.log(`View users clicked for receiver: ${receiver.mac}`);
    
    // First set the state
    setSelectedReceiver(receiver.mac);
    setSelectedReceiverName(receiver.name);
    
    // Wait for state to update using a small timeout
    setTimeout(() => {
      // Then open the modal
      const viewReceiverUsersModal = document.getElementById('viewReceiverUsersModal');
      if (viewReceiverUsersModal) {
        const modal = new window.bootstrap.Modal(viewReceiverUsersModal);
        modal.show();
      }
    }, 50); // Small delay to ensure state is updated
  };
  
  // Add cell renderer for the View Users column
  const viewUsersCell = ({ row }) => {
    const receiverId = row.original.id;
    if (!receiverId) return null;
    
    return (
      <button
        className="btn p-0 viewUsersBtn"
        onClick={() => handleViewUsersClick(row.original)}
      >
        <FormattedMessage id="VIEW_USERS" />
      </button>
    );
  };

  // Update your table columns to include the cell renderer
  const tableColumnArray = [
    {
      Header: tableCheckbox,
      id: "checkbox",
      accessor: "checked",
      Cell: tableCheckbox,
      isVisible: true,
      width: 63,
      maxWidth: 63,
    },
    {
      Header: "DEVICE_NAME",
      id: "name",
      accessor: "name",
      isVisible: true,
    },
    {
      Header: "SITE_NAME",
      id: "sitesName",
      accessor: "sitesName",
      isVisible: true,
    },
    {
      Header: "GROUP_NAME",
      id: "groupsName",
      accessor: "groupsName",
      isVisible: true,
    },
    {
      Header: "USERS",
      id: "viewUsers",
      accessor: null,
      Cell: viewUsersCell,
      isVisible: true,
    },
    {
      Header: "STATUS",
      id: "status",
      accessor: "status",
      Cell: ({ row }) => {
        const status = row.original.status;
        return (
          <span className={`receiver-${status}`}>
            <FormattedMessage id={status.toUpperCase()} />
          </span>
        );
      },
      isVisible: true,
    },
  ];

  const isDisabled =
    checkedKey.length === 0 || Object.keys(selectedIds).length === 0;

  const buttons = {
    footerButtons: (
      <>
        <button
          id="setAssignGroupBtn"
          type="button"
          className="btn"
          data-bs-toggle="modal"
          data-bs-target="#notificationModal"
          disabled={isDisabled}
          onClick={() => setNotificationProps(assignGroupProps)}
        >
          <span>
            <FormattedMessage id="ASSIGN_RECEIVERS" />
          </span>
        </button>

        <button
          id="setReleaseReceiversBtn"
          type="button"
          className="btn"
          data-bs-toggle="modal"
          data-bs-target="#notificationModal"
          disabled={isDisabled || selectedKey === "-2"}
          onClick={() => setNotificationProps(deAssignGroupProps)}
        >
          <span>
            <FormattedMessage id="DE_ASSIGN_RECEIVERS" />
          </span>
        </button>

        <button
          id="assignUsersToGroupsBtn"
          type="button"
          className="btn"
          disabled={checkedKey.length === 0}
          onClick={handleAssignUsersToGroupClick}
        >
          <span>
            <FormattedMessage id="ASSIGN_USERS_TO_GROUP" />
          </span>
        </button>
      </>
    ),
  };

  const tableProps = {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey: "sbmm_groups_table",
    pageType: constants.pageType.GroupManagement,
    modalId: "GroupManagement",
    columns: tableColumnArray,
    data,
    setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount,
    setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success,
    setSuccess,
    error,
    setError,
    startIndex: startIndexForTable,
    selectedIds,
    setSelectedIds,
    searchValue,
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    buttons,
  };

  const groupTreeProps = {
    groupsTreeData,
    checkedKey,
    setCheckedKey,
    selectedKey,
    setSelectedKey,
    handleDeleteGroup,
    setError
  };

  const assignUsersToGroupModalProps = {
    setResetAssignUsersToGroupModalRef,
    users,
    dataMap: { 
      selectedIds: checkedKey.reduce((acc, key) => {
        acc[key] = 1;
        return acc;
      }, {}), 
      data: {} 
    }, // Pass checked groups as dataMap
    loadTable,
    setSuccess,
    closeModal,
    groupsTreeData,
  };

  // Add the ViewReceiverUsersModal props
  const viewReceiverUsersModalProps = {
    receiverId: selectedReceiver,
    receiverName: selectedReceiverName,
    setResetViewReceiverUsersModalRef: (ref) => {
      resetViewReceiverUsersModalRef.current = ref;
    },
  };

  return (
    <>
      <NotificationModal {...notificationProps} />
      <div className="d-flex flex-column flex-grow-1 h-100">
        <div className="mes-title d-flex justify-content-between align-items-center">
          <FormattedMessage id="GROUP_MANAGEMENT" />
        </div>
        <div className="row h-100">
          <div className="col-3 h-100">
            <div
              className="card mt-auto tree-height me-3 mt-2 mb-5 flex-grow-1"
              style={{ borderRadius: "8px" }}
            >
              <div className="d-flex custom-size p-3 justify-content-between ">
                <h6 className="fw-semibold">
                  <FormattedMessage id="RECEIVERS_GROUPS" />
                </h6>
              </div>
              <GroupTree {...groupTreeProps} />
              <button
                className="btn btn-primary add-group-button"
                data-bs-toggle="modal"
                data-bs-target="#groupModal"
              >
                <img src={add} alt="Send Icon" className="me-2" />
                <FormattedMessage id="ADD_NEW_GROUP" />
              </button>
            </div>
          </div>
          <div className="col-9">
            <MesTable {...tableProps} />
          </div>
        </div>
        <AddNewGroup
          groupsTreeData={groupsTreeData}
          setSuccess={setSuccess}
          closeModal={closeModal}
          loadTable={loadTable}
          dataMap={dataMap}
        />
        <AssignUsersToGroupModal {...assignUsersToGroupModalProps} />
        <ViewReceiverUsersModal {...viewReceiverUsersModalProps} />
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(GroupManagement);
